import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { confirmVhuDecision, loadVhuBids, loadVhuLots } from "./vhuLotService";
import VhuPageGuide from "./VhuPageGuide";

const bidStatusColor = {
  GAGNANTE: "success",
  EN_COURS: "primary",
  DEPASSEE: "secondary",
  ANNULEE: "danger",
};

const sortOptions = [
  { value: "date_desc", label: "Plus recentes" },
  { value: "date_asc", label: "Plus anciennes" },
  { value: "amount_desc", label: "Montant decroissant" },
  { value: "amount_asc", label: "Montant croissant" },
  { value: "lot_asc", label: "Enchere A-Z" },
  { value: "vehicle_asc", label: "Vehicule A-Z" },
];

const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;
const bidStatusLabel = (status) => ({
  GAGNANTE: "En tête",
  EN_COURS: "En cours",
  DEPASSEE: "Dépassée",
  ANNULEE: "Annulée",
}[status] || status || "Statut indisponible");
const lotLabel = (lot) => lot?.reference || lot?.titre || (lot?.id ? `Lot #${lot.id}` : "Enchere");
const bidLotLabel = (bid) => bid.lotReference || bid.lotTitre || `Lot #${bid.lotId}`;
const bidVehicleLabel = (bid) => bid.vehiculeLabel || (bid.vehiculeId ? `Vehicule #${bid.vehiculeId}` : "Vehicule non renseigne");
const auctionState = (lot) => {
  if (!lot) return { key: "unknown", label: "État indisponible", icon: "ri-question-line", color: "secondary", closed: false };
  if (lot.status === "ANNULE") return { key: "cancelled", label: "Enchère annulée", icon: "ri-close-circle-line", color: "danger", closed: true };
  const now = new Date();
  const start = lot.dateDebut ? new Date(lot.dateDebut) : null;
  const end = lot.dateFin ? new Date(lot.dateFin) : null;
  if (lot.status === "CLOTURE" || (end && !Number.isNaN(end.getTime()) && end <= now)) {
    return { key: "closed", label: "Enchère clôturée", icon: "ri-lock-line", color: "dark", closed: true, date: lot.dateFin };
  }
  if (lot.status === "BROUILLON" || (start && !Number.isNaN(start.getTime()) && start > now)) {
    return { key: "scheduled", label: "Enchère programmée", icon: "ri-time-line", color: "warning", closed: false, date: lot.dateDebut };
  }
  return { key: "open", label: "Enchère ouverte", icon: "ri-live-line", color: "success", closed: false, date: lot.dateFin };
};
const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

function VhuBidOffersPage() {
  const [lots, setLots] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lotFilter, setLotFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [auctionFilter, setAuctionFilter] = useState("");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [decidingVehicle, setDecidingVehicle] = useState(null);
  const [decisionConfirm, setDecisionConfirm] = useState({ open: false, bid: null, decision: null });
  const [businessAlert, setBusinessAlert] = useState({ open: false, message: "" });

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const [lotResponse, bidResponse] = await Promise.all([
        loadVhuLots(),
        loadVhuBids(),
      ]);
      setLots(lotResponse?.data?.body || []);
      setBids(bidResponse?.data?.body || []);
    } finally {
      setLoading(false);
    }
  };

  const lotById = useMemo(() => new Map(lots.map((lot) => [String(lot.id), lot])), [lots]);

  const visibleBids = useMemo(() => {
    const search = query.trim().toLowerCase();
    const filtered = bids.filter((bid) => {
      const matchesLot = !lotFilter || String(bid.lotId) === String(lotFilter);
      const matchesVehicle = !vehicleFilter || String(bid.vehiculeId) === String(vehicleFilter);
      const matchesStatus = !statusFilter || bid.status === statusFilter;
      const matchesAuction = !auctionFilter || auctionState(lotById.get(String(bid.lotId))).key === auctionFilter;
      const matchesQuery = !search || [
        bid.ferrailleurNom,
        bid.lotReference,
        bid.lotTitre,
        bid.vehiculeLabel,
        bid.commentaire,
      ].filter(Boolean).join(" ").toLowerCase().includes(search);
      return matchesLot && matchesVehicle && matchesStatus && matchesAuction && matchesQuery;
    });

    return [...filtered].sort((left, right) => {
      if (sortBy === "amount_desc") return Number(right.montant || 0) - Number(left.montant || 0);
      if (sortBy === "amount_asc") return Number(left.montant || 0) - Number(right.montant || 0);
      if (sortBy === "date_asc") return new Date(left.dateOffre || 0) - new Date(right.dateOffre || 0);
      if (sortBy === "lot_asc") return bidLotLabel(left).localeCompare(bidLotLabel(right), "fr", { sensitivity: "base" });
      if (sortBy === "vehicle_asc") return bidVehicleLabel(left).localeCompare(bidVehicleLabel(right), "fr", { sensitivity: "base" });
      return new Date(right.dateOffre || 0) - new Date(left.dateOffre || 0);
    });
  }, [bids, lotFilter, vehicleFilter, statusFilter, auctionFilter, query, sortBy, lotById]);

  const vehicleOptions = useMemo(() => {
    const byId = new Map();
    lots.forEach((lot) => {
      if (lotFilter && String(lot.id) !== String(lotFilter)) return;
      (lot.vehicules || []).forEach((vehicle) => {
        if (!vehicle?.id) return;
        byId.set(String(vehicle.id), [vehicle.marque, vehicle.modele, vehicle.immatriculation || vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`);
      });
    });
    bids.forEach((bid) => {
      if (lotFilter && String(bid.lotId) !== String(lotFilter)) return;
      if (bid.vehiculeId && !byId.has(String(bid.vehiculeId))) {
        byId.set(String(bid.vehiculeId), bidVehicleLabel(bid));
      }
    });
    return Array.from(byId, ([id, label]) => ({ id, label })).sort((left, right) => left.label.localeCompare(right.label, "fr", { sensitivity: "base" }));
  }, [lots, bids, lotFilter]);

  const summary = useMemo(() => {
    const bestAmount = visibleBids.reduce((max, bid) => Math.max(max, Number(bid.montant || 0)), 0);
    const vehicleCount = new Set(visibleBids.map((bid) => bid.vehiculeId).filter(Boolean).map(String)).size;
    const lotCount = new Set(visibleBids.map((bid) => bid.lotId).filter(Boolean).map(String)).size;
    const winningCount = visibleBids.filter((bid) => bid.isBestBid || bid.status === "GAGNANTE").length;
    const closedLotCount = new Set(visibleBids
      .filter((bid) => auctionState(lotById.get(String(bid.lotId))).closed)
      .map((bid) => String(bid.lotId))).size;
    return { bestAmount, lotCount, vehicleCount, winningCount, closedLotCount };
  }, [visibleBids, lotById]);

  const resetFilters = () => {
    setLotFilter("");
    setVehicleFilter("");
    setStatusFilter("");
    setAuctionFilter("");
    setQuery("");
    setSortBy("date_desc");
  };

  const requestDecision = (bid, decision) => {
    setDecisionConfirm({ open: true, bid, decision });
  };

  const confirmDecision = async () => {
    const { bid, decision } = decisionConfirm;
    if (!bid || !decision) return;
    setDecisionConfirm({ open: false, bid: null, decision: null });
    setDecidingVehicle(bid.vehiculeId);
    try {
      await confirmVhuDecision(bid.lotId, {
        vehiculeId: bid.vehiculeId,
        decision,
        winningBidId: decision === "CEDE" ? bid.id : null,
      });
      await loadPageData();
    } catch (error) {
      setBusinessAlert({
        open: true,
        message: error?.response?.data?.message
          || error?.response?.data?.body?.message
          || error?.message
          || "La décision n'a pas pu être enregistrée.",
      });
    } finally {
      setDecidingVehicle(null);
    }
  };

  return (
    <div className="page-content auction-page vhu-lots-page vhu-offers-page">
      <Container fluid>
        <Breadcrumbs title="Offres VHU" breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Offres VHU", link: "#" }]} />

        <Row className="g-3 mb-3">
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat success">
              <span>Offres affichées</span>
              <strong>{visibleBids.length}</strong>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat info">
              <span>Enchères clôturées</span>
              <strong>{summary.closedLotCount} <small>/ {summary.lotCount}</small></strong>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat warning">
              <span>Véhicules</span>
              <strong>{summary.vehicleCount}</strong>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat dark">
              <span>Meilleure offre</span>
              <strong>{moneyLabel(summary.bestAmount)}</strong>
            </div>
          </Col>
        </Row>

        <Card className="mb-4 vhu-list-toolbar vhu-offer-filters">
          <CardBody>
            <div className="vhu-filter-head">
              <div>
                <span>Analyse des offres</span>
                <h5>Filtrer par enchere et vehicule</h5>
              </div>
              <button type="button" className="btn btn-light btn-sm" onClick={resetFilters}>
                <i className="ri-refresh-line me-1" />
                Réinitialiser
              </button>
            </div>
            <Row className="g-3 align-items-end">
              <Col xl={2} md={6}>
                <Label>Enchere</Label>
                <Input type="select" value={lotFilter} onChange={(event) => {
                  setLotFilter(event.target.value);
                  setVehicleFilter("");
                }}>
                  <option value="">Toutes les encheres</option>
                  {lots.map((lot) => (
                    <option key={lot.id} value={lot.id}>{lotLabel(lot)}</option>
                  ))}
                </Input>
              </Col>
              <Col xl={2} md={6}>
                <Label>Vehicule</Label>
                <Input type="select" value={vehicleFilter} onChange={(event) => setVehicleFilter(event.target.value)}>
                  <option value="">Tous les vehicules</option>
                  {vehicleOptions.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>{vehicle.label}</option>
                  ))}
                </Input>
              </Col>
              <Col xl={2} md={6}>
                <Label>Statut</Label>
                <Input type="select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">Tous les statuts</option>
                  <option value="GAGNANTE">Gagnante</option>
                  <option value="EN_COURS">En cours</option>
                  <option value="DEPASSEE">Depassee</option>
                  <option value="ANNULEE">Annulee</option>
                </Input>
              </Col>
              <Col xl={2} md={6}>
                <Label>État enchère</Label>
                <Input type="select" value={auctionFilter} onChange={(event) => setAuctionFilter(event.target.value)}>
                  <option value="">Tous les états</option>
                  <option value="open">Ouverte</option>
                  <option value="scheduled">Programmée</option>
                  <option value="closed">Clôturée</option>
                  <option value="cancelled">Annulée</option>
                </Input>
              </Col>
              <Col xl={2} md={6}>
                <Label>Tri</Label>
                <Input type="select" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Input>
              </Col>
              <Col xl={2} md={12}>
                <Label>Recherche</Label>
                <div className="vhu-search-control mb-0">
                  <i className="ri-search-line" />
                  <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Acheteur, lot..." />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : !visibleBids.length ? (
          <div className="vhu-empty-state">
            <i className="ri-auction-line" />
            <strong>Aucune offre a afficher</strong>
            <span>Les offres envoyees depuis le mobile apparaitront ici.</span>
          </div>
        ) : (
          <Card className="vhu-bids-panel">
            <CardBody>
              <div className="vhu-bid-list">
                {visibleBids.map((bid) => {
                  const state = auctionState(lotById.get(String(bid.lotId)));
                  const canDecide = state.closed && state.key !== "cancelled";
                  return (
                  <div className={`vhu-bid-row ${bid.isBestBid ? "best" : ""} auction-${state.key}`} key={bid.id}>
                    <div className="vhu-bid-card-head">
                      <div className={`vhu-buyer-avatar ${bid.isBestBid ? "best" : ""}`}>
                        <i className={bid.isBestBid ? "ri-trophy-line" : "ri-user-3-line"} />
                      </div>
                      <div className="vhu-buyer-identity">
                        <span className="vhu-card-eyebrow">Acheteur</span>
                        <strong>{bid.ferrailleurNom || "Acheteur non renseigné"}</strong>
                        <span>{bidLotLabel(bid)}</span>
                      </div>
                      <div className="vhu-bid-amount">
                        <span>Montant proposé</span>
                        <strong>{moneyLabel(bid.montant)}</strong>
                        <small>Envoyée le {dateLabel(bid.dateOffre)}</small>
                      </div>
                      <div className={`vhu-auction-state ${state.key}`}>
                        <i className={state.icon} />
                        <span><strong>{state.label}</strong><small>{state.date ? `${state.key === "scheduled" ? "Début" : "Fin"} : ${dateLabel(state.date)}` : ""}</small></span>
                      </div>
                    </div>

                    <div className="vhu-bid-status-row">
                      {(bid.isBestBid || bid.status === "GAGNANTE") && <Badge color="success"><i className="ri-trophy-line" /> Meilleure offre</Badge>}
                      <Badge color={bidStatusColor[bid.status] || "secondary"}>{bidStatusLabel(bid.status)}</Badge>
                      {bid.decisionConfirmed && (
                        <Badge color={bid.decisionFinale === "CEDE" ? "success" : "warning"}>
                          <i className={bid.decisionFinale === "CEDE" ? "ri-checkbox-circle-line" : "ri-lock-line"} />
                          {bid.decisionFinale === "CEDE" ? `Cédé à ${bid.winningBidderNom || "l'acheteur retenu"}` : "Gardé par le propriétaire"}
                        </Badge>
                      )}
                    </div>

                    <div className="vhu-bid-info-grid">
                      <section className="vhu-bid-info-section vehicle">
                        <div className="vhu-info-section-title"><i className="ri-car-line" /><span>Véhicule concerné</span></div>
                        <strong>{bidVehicleLabel(bid)}</strong>
                        <small>Offre #{bid.id}</small>
                      </section>
                      <section className="vhu-bid-info-section">
                        <div className="vhu-info-section-title"><i className="ri-bank-card-line" /><span>Modalités proposées</span></div>
                        <div className="vhu-info-pairs">
                          <div><span>Paiement</span><strong>{bid.delaiPaiement || "-"}</strong></div>
                          <div><span>Enlèvement</span><strong>{bid.delaiEnlevement || "-"}</strong></div>
                          <div><span>Moyen</span><strong>{bid.moyenPaiementPrevu || "-"}</strong></div>
                        </div>
                      </section>
                      <section className="vhu-bid-info-section">
                        <div className="vhu-info-section-title"><i className="ri-shield-check-line" /><span>Engagements</span></div>
                        <div className="vhu-commitments">
                          <span className={bid.termsAccepted ? "valid" : "invalid"}><i className={bid.termsAccepted ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"} /> Conditions acceptées</span>
                          <span className={bid.firmOfferAccepted ? "valid" : "invalid"}><i className={bid.firmOfferAccepted ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"} /> Offre ferme</span>
                        </div>
                      </section>
                    </div>

                    {bid.commentaire && (
                      <div className="vhu-bid-comment"><b>Commentaire de l'acheteur</b><span>{bid.commentaire}</span></div>
                    )}

                    {!bid.decisionConfirmed && (
                      <div className="vhu-bid-decision-bar">
                        <div>
                          {!canDecide && state.key !== "cancelled" && <span className="vhu-decision-hint"><i className="ri-information-line" /> Décision disponible après la clôture.</span>}
                        </div>
                        <div className="vhu-decision-actions">
                          <Button size="sm" color="success" disabled={!canDecide || decidingVehicle === bid.vehiculeId} title={!canDecide ? "Disponible après la clôture de l'enchère" : ""} onClick={() => requestDecision(bid, "CEDE")}><i className="ri-check-line me-1" />Céder à cette offre</Button>
                          {bid.isBestBid && <Button size="sm" color="warning" outline disabled={!canDecide || decidingVehicle === bid.vehiculeId} title={!canDecide ? "Disponible après la clôture de l'enchère" : ""} onClick={() => requestDecision(bid, "GARDE")}><i className="ri-lock-line me-1" />Garder</Button>}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        )}
      </Container>

      <Modal isOpen={decisionConfirm.open} toggle={() => setDecisionConfirm({ open: false, bid: null, decision: null })} centered>
        <ModalHeader toggle={() => setDecisionConfirm({ open: false, bid: null, decision: null })}>
          Confirmer la décision finale
        </ModalHeader>
        <ModalBody>
          {decisionConfirm.decision === "CEDE"
            ? <>Confirmez-vous la cession de ce véhicule à <strong>{decisionConfirm.bid?.ferrailleurNom || "cet acheteur"}</strong> pour <strong>{moneyLabel(decisionConfirm.bid?.montant)}</strong> ?</>
            : <>Confirmez-vous que ce véhicule sera gardé par le propriétaire ?</>}
          <div className="text-muted mt-2">Cette décision sera visible par tous les participants.</div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDecisionConfirm({ open: false, bid: null, decision: null })}>Annuler</Button>
          <Button color="primary" onClick={confirmDecision}>Confirmer</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={businessAlert.open} toggle={() => setBusinessAlert({ open: false, message: "" })} centered>
        <ModalHeader toggle={() => setBusinessAlert({ open: false, message: "" })}>Action impossible</ModalHeader>
        <ModalBody>
          <div className="d-flex align-items-start gap-3">
            <i className="ri-error-warning-line text-warning fs-3" />
            <div>{businessAlert.message}</div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setBusinessAlert({ open: false, message: "" })}>Fermer</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default VhuBidOffersPage;
